import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Refresh } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';

import PermissionedComponent from '~/components/Common/PermissionedComponent';
import UserSearchWidget from '~/components/Common/UserSearchWidget';
import Accordion from '~/components/v2/common/Accordion/Accordion';

import { ROLE_PROMO_ADMIN_USER } from '~/consts/roles';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import { PromoRole } from '~/services/PromoService';

import PromoRoleSelector from './PromoRoleSelector';

function AddPromoUserToRoleForm({
  addPromoUserToRole,
  promoRoles,
  onRefresh,
  isDirty = false,
}: {
  promoRoleId?: string;
  addPromoUserToRole: (promoRoleId: string, userId: string) => Promise<void>;
  promoRoles: Array<PromoRole>;
  onRefresh?: () => void;
  isDirty?: boolean;
}) {
  const [userId, setUserId] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [userSearchValue, setUserSearchValue] = useState<string>('');
  const { tenant } = useCurrentTenant();
  const { enqueueSnackbar } = useSnackbar();

  const fetchUser = useCallback(
    async (value: string) => {
      setUserId(value);
    },
    [tenant.brand],
  );

  const handleUserSearchSelect = useCallback(
    (id, searchQuery: string) => {
      fetchUser(searchQuery);
    },
    [fetchUser],
  );

  const handleSubmit = useCallback(async () => {
    if (!userId || !roleId) return;

    try {
      await addPromoUserToRole(roleId, userId);
      // Keep form inputs populated after successful submission
      // This allows users to add the same role to multiple users or add multiple roles to the same user
    } catch (error) {
      // Don't clear form on error - let user see what they entered
      enqueueSnackbar('Failed to add user to role:', error);
    }
  }, [addPromoUserToRole, userId, roleId]);

  return (
    <Accordion title="Assign Role to User" defaultExpanded={false}>
      <PermissionedComponent
        requiredRoles={[ROLE_PROMO_ADMIN_USER]}
        fallback={<Button disabled={true}>Assign Role to User (promo admin required)</Button>}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
          {isDirty && onRefresh && (
            <Button
              startIcon={<Refresh />}
              onClick={onRefresh}
              variant="outlined"
              size="small"
              title="Refresh data to see latest changes"
            >
              Refresh
            </Button>
          )}

          <PromoRoleSelector id="roleId" selectedRoleId={roleId} promoRoles={promoRoles} onChange={setRoleId} />

          <UserSearchWidget
            id="fk_customer_id"
            label="Name / Email / Customer Support Code / User ID"
            formErrors={[]}
            loading={false}
            onChange={handleUserSearchSelect}
            passedAutocompleteValue={userSearchValue}
            setPassedAutocompleteValue={setUserSearchValue}
          />
          <Button variant="contained" onClick={handleSubmit} disabled={!userId || !roleId} sx={{ maxWidth: '30rem' }}>
            Assign Role to User
          </Button>
        </Stack>
      </PermissionedComponent>
    </Accordion>
  );
}

export default AddPromoUserToRoleForm;
